import { ChangeEvent } from 'react';
import { FormControl, FormGroup, FormHelperText } from '@mui/material';
import { Control, useController } from 'react-hook-form';
import { CoreBox, CoreCheckbox, CoreFormControlLabel, CoreTooltip } from '@youscience/core';

import { Option } from '@interfaces';

interface CheckboxGroupProps {
  name: string;
  // eslint-disable-next-line
  control: Control<any>;
  options: Option[];
  isRowPosition?: boolean;
  onChange?: (values: string[]) => void;
  isShowTooltip?: boolean;
}

const sxStyles = {
  controlLabel: {
    width: '100%',
  },
  label: {
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
};

const EMPTY_ARRAY: string[] = [];

export const CheckboxGroup = (props: CheckboxGroupProps) => {
  const { name, control, options, isRowPosition = false, onChange, isShowTooltip = false } = props;
  const { field, fieldState } = useController({
    name,
    control,
    defaultValue: EMPTY_ARRAY,
  });
  const { ref, ...rest } = field;

  const handleChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const { value } = event.target;
    let values = [];

    if (checked) {
      values = [...(field.value as string[]), value];
      field.onChange(values);
    } else {
      values = (field.value as string[]).filter((oldValue) => oldValue !== value);
      field.onChange(values);
    }

    if (onChange) {
      onChange(values);
    }
  };

  return (
    <FormControl component="fieldset" variant="standard" fullWidth>
      <FormGroup row={isRowPosition}>
        {options.map(({ label, value }) => (
          <CoreFormControlLabel
            key={value}
            sx={sxStyles.controlLabel}
            label={
              isShowTooltip ? (
                <CoreTooltip title={label}>
                  <CoreBox component="span" sx={sxStyles.label}>
                    {label}
                  </CoreBox>
                </CoreTooltip>
              ) : (
                <CoreBox component="span" sx={sxStyles.label}>
                  {label}
                </CoreBox>
              )
            }
            inputRef={ref}
            disableTypography
            control={
              <CoreCheckbox
                {...rest}
                value={value}
                checked={(field.value as string[]).includes(value)}
                onChange={handleChange}
                disableRipple
                testId={`checkbox_${value}`}
              />
            }
          />
        ))}

        {fieldState.error && <FormHelperText error>{fieldState.error.message}</FormHelperText>}
      </FormGroup>
    </FormControl>
  );
};
